import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import type { CreateTransportRoutePayload } from 'contracts/transport-routes';
import { useTranslation } from 'hooks';
import {
  useCarrierOptions,
  useYesOrNoOptions,
  useCompany,
} from 'hooks/api/options';
import {
  useBackwardTripState,
  useSelectedGeneralCompanyState,
} from 'pages/Settings/transport-routes/hooks';
import { Card, CardContent } from 'components/ui/card';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SearchableSelectGs, ComboboxGs } from 'components/ui/Forms';
import {
  TripManager,
  type TripManagerRef,
} from 'pages/Settings/transport-routes/shared-components';

import { formSchema, type FormSchema } from './form-schema';
import { translations } from './translations';

type Ref = {
  getFormData: () => Promise<CreateTransportRoutePayload>;
};

type Props = React.HTMLAttributes<HTMLDivElement>;

export type CreateRouteFormRef = Ref;

export const CreateRouteForm = React.forwardRef<Ref, Props>((props, ref) => {
  const { getTranslation: t } = useTranslation(translations);
  const carrierOptions = useCarrierOptions();
  const yesOrNoOptions = useYesOrNoOptions();
  const { fetchCompanies } = useCompany();
  const { clearBackwardTripState } = useBackwardTripState();
  const { setSelectedGeneralCompany, clearSelectedGeneralCompany } =
    useSelectedGeneralCompanyState();

  const [hasBackwardTrip, setHasBackwardTrip] = React.useState<
    boolean | undefined
  >();
  const forwardTripManagerRef = React.useRef<TripManagerRef>(null);
  const backwardTripManagerRef = React.useRef<TripManagerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const companiesQuery = useQuery({
    queryKey: ['fetch-general-company-options'],
    queryFn: () =>
      fetchCompanies({ all: 1, type: 'CD', picking_manage: 1 }),
  });

  const generalCompanyOptions = React.useMemo(
    () =>
      companiesQuery.data?.options?.map((option) => ({
        name: option.label,
        value: String(option.value),
      })) ?? [],
    [companiesQuery.data?.options]
  );

  const onGeneralCompanyChange = React.useCallback(
    (selectedCompanyId: string) => {
      const selectedCompany =
        companiesQuery.data?.data.find(
          (company) => String(company.id) === selectedCompanyId
        ) ?? null;

      setSelectedGeneralCompany(selectedCompany);
    },
    [companiesQuery.data?.data, setSelectedGeneralCompany]
  );

  const getFormValues = React.useCallback(async (): Promise<FormSchema> => {
    const isFormValid = await form.trigger();

    if (!isFormValid) {
      throw new Error('Create route form has invalid data');
    }

    return form.getValues();
  }, [form]);

  const getFormData =
    React.useCallback(async (): Promise<CreateTransportRoutePayload> => {
      try {
        const [formValues, forwardTripData, backwardTripData] =
          await Promise.all([
            getFormValues(),
            forwardTripManagerRef.current?.getFormData()!,
            hasBackwardTrip
              ? backwardTripManagerRef.current?.getFormData()!
              : null,
          ]);

        return {
          name: formValues.name,
          carrier_id: +formValues.carrier_id,
          general_company_id: +formValues.general_company_id,
          forward_trip: forwardTripData,
          backward_trip: backwardTripData,
        };
      } catch (e: any) {
        throw new Error(e.message);
      }
    }, [getFormValues, hasBackwardTrip]);

  React.useEffect(() => {
    if (hasBackwardTrip !== undefined) {
      clearBackwardTripState();
    }
  }, [hasBackwardTrip, clearBackwardTripState]);

  React.useEffect(() => {
    return () => {
      clearSelectedGeneralCompany();
    };
  }, [clearSelectedGeneralCompany]);

  React.useImperativeHandle(
    ref,
    () => ({
      getFormData,
    }),
    [getFormData]
  );

  return (
    <div {...props}>
      <Card>
        <CardContent className={'pt-6'}>
          <Form {...form}>
            <form>
              <div className={'grid grid-cols-4 gap-4'}>
                <FormField
                  name={'name'}
                  control={form.control}
                  render={({ field }) => (
                    <InputGs {...field} label={t('name')} />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'carrier_id'}
                  render={({ field }) => (
                    <ComboboxGs
                      label={t('carrier')}
                      form={form}
                      fieldName={field.name}
                      fieldValue={field.value}
                      content={carrierOptions.singleSelectOptions}
                      loading={carrierOptions.loading}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'general_company_id'}
                  render={({ field }) => (
                    <ComboboxGs
                      label={t('generalCompany')}
                      form={form}
                      fieldName={field.name}
                      fieldValue={field.value}
                      content={generalCompanyOptions}
                      loading={companiesQuery.isLoading}
                      onChange={onGeneralCompanyChange}
                    />
                  )}
                />
                <FormField
                  name={'backward_trip'}
                  control={form.control}
                  render={({ field }) => (
                    <SearchableSelectGs
                      {...field}
                      label={t('freightReturn')}
                      onValueChange={(v) => {
                        if (!v.trim()) return;
                        field.onChange(v);
                        setHasBackwardTrip(v === '1');
                      }}
                      content={yesOrNoOptions.singleSelectOptions}
                    />
                  )}
                />
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      {hasBackwardTrip !== undefined && (
        <TripManager
          ref={forwardTripManagerRef}
          tripDirection={'forward'}
          title={
            hasBackwardTrip ? t('forwardTripTitle') : t('forwardTripOnlyTitle')
          }
          className={'mt-6'}
        />
      )}
      {hasBackwardTrip === true && (
        <TripManager
          ref={backwardTripManagerRef}
          tripDirection={'backward'}
          title={t('backwardTripTitle')}
          className={'mt-6'}
        />
      )}
    </div>
  );
});
CreateRouteForm.displayName = 'CreateRouteForm';
